// src/pages/Register.jsx
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const DAR_COLORS = {
  cream: "#F5F0E8",
  warmBeige: "#EDE5D5",
  gold: "#C9A96E",
  goldLight: "#E8D5A3",
  goldDark: "#9E7A3F",
  deepGreen: "#1E3A2F",
  midGreen: "#2D5A40",
  textDark: "#2C2416",
  textMid: "#6B5A3E",
  textLight: "#A89070",
  white: "#FDFAF5",
};

// Centralised API base URL (mirrors Login.jsx)
const API = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const getToken = () => localStorage.getItem("access_token") || "";

// Shared apiFetch helper
async function apiFetch(path, opts = {}) {
  const headers = { Authorization: `Bearer ${getToken()}`, ...opts.headers };
  if (!(opts.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    // Surface express-validator array errors cleanly
    if (Array.isArray(err.errors) && err.errors.length) {
      throw new Error(err.errors.map(e => e.msg).join(", "));
    }
    throw new Error(err.error || err.message || res.statusText);
  }
  if (res.status === 204) return null;
  return res.json();
}

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes spinReverse {
    from { transform: rotate(0deg); }
    to   { transform: rotate(-360deg); }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(201,169,110,0.0); }
    50%       { box-shadow: 0 0 24px 8px rgba(201,169,110,0.2); }
  }
  @keyframes floatGold {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50%       { transform: translateY(-14px) rotate(2deg); }
  }
  @keyframes checkIn {
    0%   { transform: scale(0) rotate(-90deg); opacity: 0; }
    70%  { transform: scale(1.2) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }
  @keyframes stepPulse {
    0%, 100% { transform: scale(1); }
    50%       { transform: scale(1.05); }
  }

  /* ── Responsive layout ── */
  .reg-root {
    min-height: 100vh;
    display: flex;
    flex-direction: row;
    position: relative;
    overflow: hidden;
  }
  .reg-brand-panel {
    width: 42%;
    order: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 44px;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
  }
  .reg-form-panel {
    flex: 1;
    order: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 50px 48px;
    position: relative;
  }
  .reg-form-inner {
    width: 100%;
    max-width: 440px;
  }
  .reg-name-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
  }

  /* Tablet: stack, brand on top */
  @media (max-width: 900px) {
    .reg-root { flex-direction: column; }
    .reg-brand-panel {
      width: 100%;
      order: 1;
      padding: 40px 24px 32px;
      min-height: unset;
    }
    .reg-form-panel {
      order: 2;
      padding: 32px 24px 48px;
    }
  }

  /* Mobile: single column name fields */
  @media (max-width: 480px) {
    .reg-brand-panel  { padding: 32px 20px 24px; }
    .reg-form-panel   { padding: 24px 20px 40px; }
    .reg-form-inner   { max-width: 100%; }
    .reg-name-row     { grid-template-columns: 1fr; }
  }
`;

function StarPattern({ style: extraStyle, opacity = 0.1, size = 220, animate = false, speed = 25 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 220 220"
      style={{
        position: "absolute", opacity, pointerEvents: "none",
        animation: animate ? `floatGold ${speed}s ease-in-out infinite` : undefined,
        ...extraStyle,
      }}
      xmlns="http://www.w3.org/2000/svg">
      <g stroke={DAR_COLORS.gold} strokeWidth="0.8" fill="none">
        {[0,30,60,90,120,150].map(a => (
          <line key={a} x1="110" y1="110"
            x2={110 + 100 * Math.cos(a * Math.PI / 180)}
            y2={110 + 100 * Math.sin(a * Math.PI / 180)} />
        ))}
        <circle cx="110" cy="110" r="100" strokeDasharray="4 6"/>
        <circle cx="110" cy="110" r="68"/>
        <circle cx="110" cy="110" r="42" strokeDasharray="2 4"/>
        {[0,45,90,135,180,225,270,315].map(a => (
          <circle key={a}
            cx={110 + 68 * Math.cos(a * Math.PI / 180)}
            cy={110 + 68 * Math.sin(a * Math.PI / 180)}
            r="4" fill={`${DAR_COLORS.gold}33`} />
        ))}
      </g>
    </svg>
  );
}

const STRENGTH_LEVELS = [
  { key: "weak",   color: "#EF5350", width: "25%"  },
  { key: "fair",   color: "#FFA726", width: "50%"  },
  { key: "good",   color: "#66BB6A", width: "75%"  },
  { key: "strong", color: "#26A69A", width: "100%" },
];

function getPasswordStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score - 1;
}

// ── Google OAuth button ──────────────────────────────────────────────────────
function GoogleButton({ mounted, label }) {
  const [hovered, setHovered] = useState(false);

  const handleGoogleRegister = () => {
    // Server handles OAuth and redirects back to /login?token=...&user=...
    window.location.href = `${API}/api/auth/google`;
  };

  return (
    <button
      type="button"
      onClick={handleGoogleRegister}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%",
        padding: "14px 24px",
        background: hovered ? "#f5f5f5" : DAR_COLORS.white,
        border: `1.5px solid ${hovered ? DAR_COLORS.gold : `${DAR_COLORS.gold}40`}`,
        borderRadius: 10,
        color: DAR_COLORS.textDark,
        fontSize: 13,
        fontFamily: "'Jost', sans-serif",
        fontWeight: 400,
        letterSpacing: 0.5,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        transition: "all 0.3s ease",
        boxShadow: hovered ? `0 4px 16px ${DAR_COLORS.gold}20` : "0 2px 8px rgba(0,0,0,0.06)",
        opacity: mounted ? 1 : 0,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      {label}
    </button>
  );
}

export default function Register({ onNavigateToLogin, onAuthSuccess }) {
  const { t } = useTranslation();

  const STEPS = [
    { id: 1, labelKey: "register.step_identity" },
    { id: 2, labelKey: "register.step_security" },
  ];

  const [step, setStep] = useState(1);
  const [mounted, setMounted] = useState(false);
  const [animatingStep, setAnimatingStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "",
    password: "", confirmPassword: "",
    agreeTerms: false,
  });
  const [focusedField, setFocusedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Validate just the current step before advancing
  const validateStep = (which) => {
    if (which === 1) {
      if (!form.firstName.trim() && !form.lastName.trim()) {
        return t("register.error_name", { defaultValue: "Please enter your name." });
      }
      if (!form.email.trim()) {
        return t("register.error_email", { defaultValue: "Please enter your email." });
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
        return t("register.error_email_invalid", { defaultValue: "Please enter a valid email address." });
      }
      return null;
    }
    if (which === 2) {
      if (!form.password || form.password.length < 6) {
        return t("register.error_password", { defaultValue: "Password must be at least 6 characters." });
      }
      if (form.password !== form.confirmPassword) {
        return t("register.passwords_no_match");
      }
      if (!form.agreeTerms) {
        return t("register.error_terms");
      }
      return null;
    }
    return null;
  };

  const goToStep = (next) => {
    // When moving forward, validate the current step first
    if (next > step) {
      const msg = validateStep(step);
      if (msg) { setError(msg); return; }
    }
    setError("");
    setAnimatingStep(true);
    setTimeout(() => { setStep(next); setAnimatingStep(false); }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Re-run validation in order so users see the first problem
    const msg1 = validateStep(1);
    if (msg1) { setError(msg1); setStep(1); return; }
    const msg2 = validateStep(2);
    if (msg2) { setError(msg2); return; }

    setLoading(true);
    try {
      const name = [form.firstName, form.lastName].filter(Boolean).join(" ").trim() || undefined;

      // POST /api/auth/register — server accepts { email, password, name }
      // role is intentionally omitted; server defaults to 'USER'
      const data = await apiFetch("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
          name,
        }),
      });

      // Consistent token key across Login.jsx / Register.jsx / getToken()
      localStorage.setItem("access_token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      if (typeof onAuthSuccess === "function") {
        onAuthSuccess({ token: data.token, user: data.user });
      } else {
        setDone(true);
      }
    } catch (err) {
      setError(err.message || t("register.error_network"));
    } finally {
      setLoading(false);
    }
  };

  const strength = form.password ? getPasswordStrength(form.password) : -1;
  const progressPct = ((step - 1) / (STEPS.length - 1)) * 100;

  if (done) return <SuccessScreen name={form.firstName} onLogin={onNavigateToLogin} t={t} />;

  return (
    <>
      <style>{globalStyles}</style>
      <div className="reg-root" style={{
        background: `linear-gradient(150deg, ${DAR_COLORS.cream} 0%, ${DAR_COLORS.warmBeige} 60%, #E8E0D0 100%)`,
        fontFamily: "'Jost', sans-serif",
      }}>

        <StarPattern style={{ top: -50, right: -50 }} size={280} animate opacity={0.09} speed={28} />
        <StarPattern style={{ bottom: -80, left: -60 }} size={300} animate opacity={0.07} speed={35} />
        <StarPattern style={{ top: "35%", left: "10%" }} size={140} animate opacity={0.06} speed={22} />

        {/* Top shimmer bar */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3, zIndex: 10,
          background: `linear-gradient(90deg, transparent, ${DAR_COLORS.gold}, ${DAR_COLORS.goldLight}, ${DAR_COLORS.gold}, transparent)`,
          backgroundSize: "200% 100%",
          animation: "shimmer 3s linear infinite",
        }} />

        {/* ── Brand panel ── */}
        <div
          className="reg-brand-panel"
          style={{
            background: `linear-gradient(155deg, ${DAR_COLORS.deepGreen} 0%, #152B22 100%)`,
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateX(0)" : "translateX(40px)",
            transition: "all 0.9s cubic-bezier(0.23,1,0.32,1)",
          }}
        >
          <div style={{
            position: "absolute", top: 0, insetInlineStart: 0, width: 3, height: "100%",
            background: `linear-gradient(180deg, transparent, ${DAR_COLORS.gold}, transparent)`,
            backgroundSize: "100% 200%",
            animation: "shimmer 4s linear infinite",
          }} />
          <div style={{ position: "absolute", inset: 0, opacity: 0.07, pointerEvents: "none" }}>
            <StarPattern style={{ top: -30, right: -30 }} size={250} opacity={1} animate speed={38} />
            <StarPattern style={{ bottom: -50, left: -50 }} size={280} opacity={1} animate speed={30} />
          </div>

          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 36, position: "relative", zIndex: 1 }}>
            <div style={{
              width: 150, height: 150, borderRadius: "50%",
              border: `1.5px solid ${DAR_COLORS.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 18px", position: "relative",
              animation: "pulseGlow 3s ease-in-out infinite",
            }}>
              <img src="/logo 1.png" alt="Dar Oulmes" style={{ width: 140, height: 140, borderRadius: "50%", objectFit: "cover" }} />
              <div style={{
                position: "absolute", inset: -8, borderRadius: "50%",
                border: `1px solid ${DAR_COLORS.gold}44`,
                borderTopColor: DAR_COLORS.gold,
                animation: "spin 8s linear infinite",
              }} />
              <div style={{
                position: "absolute", inset: -16, borderRadius: "50%",
                border: `1px solid ${DAR_COLORS.gold}22`,
                borderBottomColor: `${DAR_COLORS.gold}88`,
                animation: "spinReverse 14s linear infinite",
              }} />
            </div>

            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 30, fontWeight: 300, letterSpacing: 6,
              color: DAR_COLORS.white, textTransform: "uppercase", marginBottom: 5,
            }}>
              Dar Oulmes
            </div>
            <div style={{ fontSize: 10, letterSpacing: 3, color: DAR_COLORS.goldLight, textTransform: "uppercase" }}>
              {t("brand.tagline")}
            </div>
          </div>

          <div style={{
            width: 50, height: 1,
            background: `linear-gradient(90deg, transparent, ${DAR_COLORS.gold}, transparent)`,
            marginBottom: 32,
          }} />

          {/* Step guide */}
          <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 260 }}>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 17, fontStyle: "italic", color: `${DAR_COLORS.white}BB`,
              textAlign: "center", marginBottom: 28, lineHeight: 1.6,
            }}>
              {t("register.brand_subtitle")}
            </p>

            {STEPS.map((s, i) => (
              <div key={s.id} style={{
                display: "flex", alignItems: "center", gap: 14,
                marginBottom: 18,
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateX(0)" : "translateX(20px)",
                transition: `all 0.6s ease ${0.4 + i * 0.12}s`,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: step > s.id
                    ? `linear-gradient(135deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldDark})`
                    : "transparent",
                  border: step === s.id
                    ? `2px solid ${DAR_COLORS.gold}`
                    : step > s.id
                      ? "none"
                      : `1px solid ${DAR_COLORS.gold}33`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13,
                  color: step >= s.id ? DAR_COLORS.white : `${DAR_COLORS.white}44`,
                  transition: "all 0.4s ease",
                  animation: step === s.id ? "stepPulse 2s ease infinite" : undefined,
                  flexShrink: 0,
                }}>
                  {step > s.id ? "✓" : s.id}
                </div>
                <div>
                  <div style={{
                    fontSize: 12, fontWeight: 400, letterSpacing: 1,
                    color: step >= s.id ? DAR_COLORS.white : `${DAR_COLORS.white}44`,
                    transition: "color 0.3s",
                  }}>
                    {t(s.labelKey)}
                  </div>
                  <div style={{ fontSize: 10, color: `${DAR_COLORS.goldLight}66`, letterSpacing: 0.5 }}>
                    {t("register.step_of", { current: s.id, total: STEPS.length })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Form panel ── */}
        <div className="reg-form-panel">
          <div
            className="reg-form-inner"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(30px)",
              transition: "all 1s cubic-bezier(0.23,1,0.32,1) 0.15s",
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <div style={{
                fontSize: 11, letterSpacing: 4, color: DAR_COLORS.gold,
                textTransform: "uppercase", marginBottom: 8,
              }}>
                {t("register.eyebrow")}
              </div>
              <h1 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 42, fontWeight: 400, color: DAR_COLORS.textDark,
                lineHeight: 1.1, letterSpacing: -1,
              }}>
                {t("register.title_prefix")}{" "}
                <em style={{ fontStyle: "italic", fontWeight: 300 }}>Dar Oulmes</em>
              </h1>
              <div style={{
                marginTop: 10, height: 2, width: mounted ? 60 : 0,
                background: `linear-gradient(90deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldLight})`,
                borderRadius: 2,
                transition: "width 0.8s cubic-bezier(0.23,1,0.32,1) 0.6s",
              }} />
            </div>

            {/* Progress bar */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                {STEPS.map(s => (
                  <span key={s.id} style={{
                    fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
                    color: step >= s.id ? DAR_COLORS.goldDark : DAR_COLORS.textLight,
                    transition: "color 0.3s",
                  }}>
                    {t(s.labelKey)}
                  </span>
                ))}
              </div>
              <div style={{ height: 4, background: `${DAR_COLORS.gold}20`, borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  background: `linear-gradient(90deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldLight})`,
                  width: `${progressPct}%`,
                  transition: "width 0.6s cubic-bezier(0.23,1,0.32,1)",
                  boxShadow: `0 0 10px ${DAR_COLORS.gold}66`,
                }} />
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <div style={{
                  padding: "12px 16px", marginBottom: 20,
                  background: "#FFF0F0", border: "1px solid #FFCDD2",
                  borderRadius: 8, fontSize: 13, color: "#C62828",
                  animation: "fadeSlideUp 0.3s ease",
                }}>
                  {error}
                </div>
              )}
              <div style={{
                opacity: animatingStep ? 0 : 1,
                transform: animatingStep ? "translateX(20px)" : "translateX(0)",
                transition: "all 0.3s ease",
              }}>
                {step === 1 && (
                  <StepOne form={form} update={update} focusedField={focusedField} setFocusedField={setFocusedField} t={t} />
                )}
                {step === 2 && (
                  <StepTwo form={form} update={update} focusedField={focusedField} setFocusedField={setFocusedField}
                    showPassword={showPassword} setShowPassword={setShowPassword}
                    showConfirm={showConfirm} setShowConfirm={setShowConfirm}
                    strength={strength} t={t} />
                )}
              </div>

              {/* Navigation buttons */}
              <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
                {step > 1 && (
                  <button type="button" onClick={() => { setError(""); goToStep(step - 1); }}
                    style={{
                      flex: "0 0 auto", padding: "14px 20px",
                      background: "transparent",
                      border: `1.5px solid ${DAR_COLORS.gold}60`,
                      borderRadius: 10, cursor: "pointer",
                      color: DAR_COLORS.textMid, fontFamily: "'Jost', sans-serif",
                      fontSize: 13, transition: "all 0.3s",
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    {t("common.back")}
                  </button>
                )}

                {step < STEPS.length ? (
                  <NextButton onClick={() => goToStep(step + 1)} label={t("common.continue")} />
                ) : (
                  <SubmitButton loading={loading} label={t("register.submit")} loadingLabel={t("register.submitting")} />
                )}
              </div>
            </form>

            {/* Divider */}
            <div style={{
              display: "flex", alignItems: "center", gap: 16,
              margin: "24px 0 16px",
            }}>
              <div style={{ flex: 1, height: 1, background: `${DAR_COLORS.gold}30` }} />
              <span style={{ fontSize: 11, color: DAR_COLORS.textLight, letterSpacing: 2 }}>
                {t("common.or")}
              </span>
              <div style={{ flex: 1, height: 1, background: `${DAR_COLORS.gold}30` }} />
            </div>

            {/* Google Sign-Up button (second registration type) */}
            <GoogleButton
              mounted={mounted}
              label={t("register.google_signup", { defaultValue: "Sign up with Google" })}
            />

            {/* Login link */}
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <span style={{ fontSize: 13, color: DAR_COLORS.textMid }}>
                {t("register.have_account")}{" "}
              </span>
              <button type="button" onClick={onNavigateToLogin}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 13, color: DAR_COLORS.deepGreen,
                  fontWeight: 500, fontFamily: "'Jost', sans-serif",
                  textDecoration: "underline",
                  textDecorationColor: `${DAR_COLORS.gold}66`,
                  textUnderlineOffset: 3,
                }}>
                {t("login.title")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Success screen ── */
function SuccessScreen({ name, onLogin, t }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  return (
    <>
      <style>{globalStyles}</style>
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(150deg, ${DAR_COLORS.deepGreen} 0%, #152B22 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Jost', sans-serif",
        position: "relative", overflow: "hidden",
      }}>
        <StarPattern style={{ top: -50, left: -50 }} size={280} animate opacity={0.08} speed={30} />
        <StarPattern style={{ bottom: -80, right: -80 }} size={320} animate opacity={0.06} speed={40} />
        <div style={{
          textAlign: "center",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0) scale(1)" : "translateY(30px) scale(0.95)",
          transition: "all 0.8s cubic-bezier(0.23,1,0.32,1)",
          padding: "0 24px",
        }}>
          <div style={{
            width: 90, height: 90, borderRadius: "50%",
            background: `linear-gradient(135deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldDark})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 28px",
            boxShadow: `0 0 40px ${DAR_COLORS.gold}44`,
            animation: "pulseGlow 2s ease infinite",
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={DAR_COLORS.white} strokeWidth="2"
              style={{ animation: "checkIn 0.5s ease 0.3s both" }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 40, fontWeight: 300, color: DAR_COLORS.white, marginBottom: 12,
          }}>
            {t("register.success_title", { name: name || t("register.success_fallback") })}
          </h2>
          <p style={{ fontSize: 13, color: `${DAR_COLORS.goldLight}99`, letterSpacing: 1, marginBottom: 36 }}>
            {t("register.success_subtitle")}
          </p>
          <button type="button" onClick={onLogin}
            style={{
              padding: "14px 36px",
              background: `linear-gradient(135deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldDark})`,
              border: "none", borderRadius: 10, cursor: "pointer",
              fontSize: 12, letterSpacing: 3, textTransform: "uppercase",
              color: DAR_COLORS.white, fontFamily: "'Jost', sans-serif",
            }}>
            {t("register.success_cta")}
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Shared sub-components ── */
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, letterSpacing: 3, color: DAR_COLORS.gold,
      textTransform: "uppercase", marginBottom: 18, fontWeight: 400,
    }}>
      {children}
    </div>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", marginBottom: 7,
        fontSize: 10, letterSpacing: 2, color: DAR_COLORS.textMid,
        textTransform: "uppercase",
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function RInput({ type = "text", value, placeholder, onChange, onFocus, onBlur, focused, icon, suffix, borderOverride }) {
  return (
    <div style={{
      display: "flex", alignItems: "center",
      border: `1.5px solid ${borderOverride || (focused ? DAR_COLORS.gold : `${DAR_COLORS.gold}35`)}`,
      borderRadius: 10,
      background: focused ? `${DAR_COLORS.gold}08` : DAR_COLORS.white,
      transition: "all 0.3s ease",
      boxShadow: focused ? `0 0 0 3px ${DAR_COLORS.gold}15` : "none",
    }}>
      {icon && (
        <div style={{
          padding: "0 12px",
          color: focused ? DAR_COLORS.gold : DAR_COLORS.textLight,
          transition: "color 0.3s", display: "flex", alignItems: "center",
        }}>
          {icon}
        </div>
      )}
      <input type={type} value={value} placeholder={placeholder}
        onChange={onChange} onFocus={onFocus} onBlur={onBlur}
        style={{
          flex: 1, padding: icon ? "13px 0" : "13px 14px",
          border: "none", outline: "none", background: "transparent",
          fontSize: 13, color: DAR_COLORS.textDark,
          fontFamily: "'Jost', sans-serif", letterSpacing: 0.3,
        }}
      />
      {suffix && <div style={{ paddingInlineEnd: 12 }}>{suffix}</div>}
    </div>
  );
}

function EyeIcon({ open }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      {open
        ? <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
      }
    </svg>
  );
}

function NextButton({ onClick, label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, padding: "15px 24px",
        background: hovered
          ? `linear-gradient(135deg, ${DAR_COLORS.midGreen}, ${DAR_COLORS.deepGreen})`
          : `linear-gradient(135deg, ${DAR_COLORS.deepGreen}, ${DAR_COLORS.midGreen})`,
        border: "none", borderRadius: 10, cursor: "pointer",
        color: DAR_COLORS.white, fontFamily: "'Jost', sans-serif",
        fontSize: 12, letterSpacing: 3, textTransform: "uppercase",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        transition: "all 0.4s ease",
        boxShadow: hovered ? `0 10px 28px ${DAR_COLORS.deepGreen}50` : `0 4px 14px ${DAR_COLORS.deepGreen}30`,
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
      }}>
      {hovered && (
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(90deg, transparent, ${DAR_COLORS.gold}22, transparent)`,
          backgroundSize: "200% 100%",
          animation: "shimmer 1.2s linear infinite",
        }} />
      )}
      <span style={{ position: "relative", zIndex: 1 }}>{label}</span>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        style={{ position: "relative", zIndex: 1 }}>
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    </button>
  );
}

function SubmitButton({ loading, label, loadingLabel }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button type="submit" disabled={loading}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, padding: "15px 24px",
        background: hovered && !loading
          ? `linear-gradient(135deg, ${DAR_COLORS.goldDark}, ${DAR_COLORS.gold})`
          : `linear-gradient(135deg, ${DAR_COLORS.gold}, ${DAR_COLORS.goldDark})`,
        border: "none", borderRadius: 10,
        cursor: loading ? "not-allowed" : "pointer",
        color: DAR_COLORS.white, fontFamily: "'Jost', sans-serif",
        fontSize: 12, letterSpacing: 3, textTransform: "uppercase",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        transition: "all 0.4s ease",
        boxShadow: hovered && !loading ? `0 10px 28px ${DAR_COLORS.gold}50` : `0 4px 14px ${DAR_COLORS.gold}30`,
        transform: hovered && !loading ? "translateY(-2px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
      }}>
      {hovered && !loading && (
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)`,
          backgroundSize: "200% 100%",
          animation: "shimmer 1.2s linear infinite",
        }} />
      )}
      {loading ? (
        <>
          <div style={{
            width: 15, height: 15,
            border: `2px solid ${DAR_COLORS.white}44`,
            borderTopColor: DAR_COLORS.white,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }} />
          <span>{loadingLabel}</span>
        </>
      ) : (
        <>
          <span style={{ position: "relative", zIndex: 1 }}>{label}</span>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
            style={{ position: "relative", zIndex: 1 }}>
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </>
      )}
    </button>
  );
}

/* ── Step 1 ── */
function StepOne({ form, update, focusedField, setFocusedField, t }) {
  return (
    <div style={{ animation: "fadeSlideUp 0.4s ease" }}>
      <SectionLabel>{t("register.step_identity")}</SectionLabel>
      <div className="reg-name-row">
        <FieldWrap label={t("register.first_name")}>
          <RInput placeholder={t("register.first_name_ph")} value={form.firstName}
            onChange={e => update("firstName", e.target.value)}
            focused={focusedField === "firstName"}
            onFocus={() => setFocusedField("firstName")} onBlur={() => setFocusedField(null)} />
        </FieldWrap>
        <FieldWrap label={t("register.last_name")}>
          <RInput placeholder={t("register.last_name_ph")} value={form.lastName}
            onChange={e => update("lastName", e.target.value)}
            focused={focusedField === "lastName"}
            onFocus={() => setFocusedField("lastName")} onBlur={() => setFocusedField(null)} />
        </FieldWrap>
      </div>
      <FieldWrap label={t("login.email_label")}>
        <RInput type="email" placeholder={t("login.email_placeholder")} value={form.email}
          onChange={e => update("email", e.target.value)}
          focused={focusedField === "email"}
          onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)}
          icon={
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          } />
      </FieldWrap>
    </div>
  );
}

/* ── Step 2 ── */
function StepTwo({ form, update, focusedField, setFocusedField, showPassword, setShowPassword, showConfirm, setShowConfirm, strength, t }) {
  const strengthInfo = strength >= 0 ? STRENGTH_LEVELS[strength] : null;
  const passwordsMatch = form.confirmPassword && form.password === form.confirmPassword;

  return (
    <div style={{ animation: "fadeSlideUp 0.4s ease" }}>
      <SectionLabel>{t("register.step_security")}</SectionLabel>
      <FieldWrap label={t("login.password_label")}>
        <RInput
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={form.password}
          onChange={e => update("password", e.target.value)}
          focused={focusedField === "password"}
          onFocus={() => setFocusedField("password")}
          onBlur={() => setFocusedField(null)}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>}
          suffix={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{ background: "none", border: "none", cursor: "pointer", color: DAR_COLORS.textLight, lineHeight: 1, padding: "0 4px" }}
            >
              <EyeIcon open={showPassword} />
            </button>
          }
        />
      </FieldWrap>

      {form.password && (
        <div style={{ marginTop: -8, marginBottom: 14, animation: "fadeSlideUp 0.3s ease" }}>
          <div style={{ height: 3, background: `${DAR_COLORS.gold}15`, borderRadius: 3, overflow: "hidden", marginBottom: 5 }}>
            <div
              style={{
                height: "100%",
                borderRadius: 3,
                background: strengthInfo?.color || DAR_COLORS.gold,
                width: strengthInfo?.width || "0%",
                transition: "width 0.5s ease, background 0.3s",
                boxShadow: `0 0 6px ${strengthInfo?.color || DAR_COLORS.gold}88`,
              }}
            />
          </div>
          <span style={{ fontSize: 10, letterSpacing: 1.5, color: strengthInfo?.color, textTransform: "uppercase" }}>
            {strengthInfo ? t(`register.strength_${strengthInfo.key}`) : ""}
          </span>
        </div>
      )}

      <FieldWrap label={t("register.confirm_password")}>
        <RInput
          type={showConfirm ? "text" : "password"}
          placeholder="••••••••"
          value={form.confirmPassword}
          onChange={e => update("confirmPassword", e.target.value)}
          focused={focusedField === "confirm"}
          onFocus={() => setFocusedField("confirm")}
          onBlur={() => setFocusedField(null)}
          borderOverride={form.confirmPassword ? (passwordsMatch ? "#66BB6A" : "#EF5350") : undefined}
          icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
          suffix={
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              style={{ background: "none", border: "none", cursor: "pointer", color: DAR_COLORS.textLight, lineHeight: 1, padding: "0 4px" }}
            >
              <EyeIcon open={showConfirm} />
            </button>
          }
        />
      </FieldWrap>
      {form.confirmPassword && !passwordsMatch && (
        <p style={{ fontSize: 11, color: "#EF5350", marginTop: -8, marginBottom: 12, letterSpacing: 0.5 }}>
          {t("register.passwords_no_match")}
        </p>
      )}

      <div style={{ marginTop: 16 }} onClick={() => update("agreeTerms", !form.agreeTerms)}>
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer",
          padding: "14px 16px",
          border: `1.5px solid ${form.agreeTerms ? DAR_COLORS.gold : `${DAR_COLORS.gold}30`}`,
          borderRadius: 10,
          background: form.agreeTerms ? `${DAR_COLORS.gold}08` : "transparent",
          transition: "all 0.3s",
        }}>
          <div style={{
            width: 18, height: 18, borderRadius: 5,
            border: `1.5px solid ${form.agreeTerms ? DAR_COLORS.gold : `${DAR_COLORS.gold}50`}`,
            background: form.agreeTerms ? DAR_COLORS.gold : "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, marginTop: 1,
          }}>
            {form.agreeTerms && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={DAR_COLORS.white} strokeWidth="3">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </div>
          <div>
            <div style={{ fontSize: 13, color: DAR_COLORS.textDark, marginBottom: 2 }}>
              {t("register.agree_terms")}
            </div>
            <div style={{ fontSize: 10, color: DAR_COLORS.textLight, letterSpacing: 0.3, lineHeight: 1.5 }}>
              {t("register.agree_terms_desc", { defaultValue: "I agree to the terms and conditions." })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}